import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { SocialPost, SocialPlatform, MediaItem } from '../../../../types/socialMediaPlanner';
import { SocialMediaPlannerService } from '../../../../services/socialMediaPlannerService';
import { socialMediaContentService } from '../../../../services/socialMediaContentService';
import ClaudeContentModal from './ClaudeContentModal';
import toast from 'react-hot-toast';
import MediaUploaderTailwind from '../../../../components/social/MediaUploaderTailwind';
import { ENV } from '../../../../config/env';
import { useAuth } from '../../../../contexts/AuthContext';

// Define PlatformPostDetail interface if not already in types file
interface PlatformPostDetail {
  platform_specific_content: string;
  scheduled_for?: string;
}

interface PostComposerProps {
  post?: SocialPost;
  onSave: (post: SocialPost) => void;
  onClose: () => void;
  // emitted when LinkedIn scheduling is requested and backend returns DB job
  onLinkedInScheduledOptimistic?: (job: { id: string; user_id?: string; platform: string; content: string; scheduled_for: string; status?: string }) => void;
}

/**
 * PostComposer Component
 * 
 * A component for creating and editing social media posts with platform-specific content.
 * Uses Tailwind CSS exclusively for styling, following project standards.
 */
const PostComposer: React.FC<PostComposerProps> = ({ post, onSave, onClose, onLinkedInScheduledOptimistic }) => {
  // Base form state with default values
  const defaultFormState: SocialPost = {
    content: '',
    platforms: [] as SocialPlatform[], // Use SocialPlatform[] instead of string[]
    hashtags: [] as string[],
    status: 'draft',
    media_urls: [] as string[],
    append_footer: false,
    footer_text: ''
  };

  // Auth
  const { getToken } = useAuth();

  // Load global defaults (from Settings) for footer on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('userSettings');
      if (!raw) return;
      const s = JSON.parse(raw) || {};
      if (s.defaultFooterEnabled || s.defaultFooterText) {
        setFormState((prev) => ({
          ...prev,
          append_footer: !!s.defaultFooterEnabled,
          footer_text: s.defaultFooterText || prev.footer_text || ''
        }));
      }
    } catch {}
  }, []);

  // Compose content with optional persistent footer (hoisted function declaration for early usage)
  function withFooter(text: string): string {
    const base = (text || '').trim();
    const footerEnabled = !!formState.append_footer && !!formState.footer_text?.trim();
    if (!footerEnabled) return base;
    return `${base}${base ? '\n\n' : ''}${formState.footer_text!.trim()}`;
  }

  // Reschedule an existing LinkedIn scheduled post
  const handleRescheduleLinkedIn = async () => {
    try {
      if (!scheduledJobId) { toast.error('No scheduled job to update'); return; }
      if (!isScheduled || !scheduleDate || !scheduleTime) {
        toast.error('Enable scheduling and choose a new date/time');
        return;
      }
      const [year, month, day] = scheduleDate.split('-').map(n => parseInt(n, 10));
      const [hour, minute] = scheduleTime.split(':').map(n => parseInt(n, 10));
      if ([year, month, day, hour, minute].some(v => Number.isNaN(v))) {
        toast.error('Invalid date or time');
        return;
      }
      const localDate = new Date(year, (month - 1), day, hour, minute, 0, 0);
      const isoUTC = localDate.toISOString();
      if (localDate.getTime() <= Date.now()) {
        toast.error('Scheduled time must be in the future');
        return;
      }
      setIsScheduling(true);
      const { jobId } = await socialMediaPlannerService.rescheduleScheduledPost(scheduledJobId, isoUTC);
      setScheduledJobId(jobId || scheduledJobId);
      toast.success('Schedule updated');
    } catch (err: any) {
      const msg = err?.userMessage || err?.message || 'Failed to update schedule';
      toast.error(msg);
    } finally {
      setIsScheduling(false);
    }
  };
  
  // Schedule LinkedIn at exact time (convert from user's local timezone to UTC for backend)
  const handleScheduleLinkedIn = async () => {
    try {
      // Preconditions
      if (!formState.platforms.includes('linkedin')) {
        toast.error('Select LinkedIn to enable scheduling');
        return;
      }
      if (!isScheduled) {
        toast.error('Enable scheduling and choose a date/time');
        return;
      }
      if (!scheduleDate || !scheduleTime) {
        toast.error('Please pick a schedule date and time (UTC)');
        return;
      }
      // Basic LinkedIn image URL validation (optional)
      if (liImageUrl && !/^https?:\/\//i.test(liImageUrl.trim())) {
        toast.error('Image URL must start with http(s)');
        return;
      }

      const content = withFooter((platformSpecificContent['linkedin']?.trim()) || formState.content?.trim() || '');
      if (!content) {
        toast.error('Please enter content to schedule');
        return;
      }

      // Build ISO timestamp from LOCAL timezone, then convert to UTC via toISOString
      const [year, month, day] = scheduleDate.split('-').map(n => parseInt(n, 10));
      const [hour, minute] = scheduleTime.split(':').map(n => parseInt(n, 10));
      if ([year, month, day, hour, minute].some(v => Number.isNaN(v))) {
        toast.error('Invalid date or time');
        return;
      }
      const localDate = new Date(year, (month - 1), day, hour, minute, 0, 0); // local time
      const isoUTC = localDate.toISOString(); // backend expects UTC ISO
      const whenMs = localDate.getTime();
      if (whenMs <= Date.now()) {
        toast.error('Scheduled time must be in the future (UTC)');
        return;
      }

      setIsScheduling(true);
      const media = liImageUrl ? { media_urls: [liImageUrl.trim()], image_alt: liImageAlt?.trim() || undefined } : undefined;
      const resp = await socialMediaPlannerService.scheduleLinkedInPost(content, isoUTC, media);
      setScheduledJobId(resp?.jobId || null);
      // Optimistic emit to parent if job payload provided
      if (resp?.job && typeof onLinkedInScheduledOptimistic === 'function') {
        onLinkedInScheduledOptimistic(resp.job);
      }
      toast.success(resp?.jobId ? `Scheduled for LinkedIn (job: ${resp.jobId})` : 'Scheduled for LinkedIn');
    } catch (err: any) {
      const msg = err?.userMessage || err?.message || 'Failed to schedule LinkedIn post';
      toast.error(msg);
    } finally {
      setIsScheduling(false);
    }
  };

  

  // Platform-specific content state
  const [platformSpecificContent, setPlatformSpecificContent] = useState<Record<string, string>>({});
  
  // References for text areas
  const commonTextareaRef = useRef<HTMLTextAreaElement>(null);
  const platformTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Component state
  const [formState, setFormState] = useState<SocialPost>(defaultFormState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleDate, setScheduleDate] = useState<string>('');
  const [scheduleTime, setScheduleTime] = useState<string>('');
  const [isScheduled, setIsScheduled] = useState<boolean>(false);
  const [showClaudeModal, setShowClaudeModal] = useState<boolean>(false);
  const [isPublishingLinkedIn, setIsPublishingLinkedIn] = useState<boolean>(false);
  const [isScheduling, setIsScheduling] = useState<boolean>(false);
  const [scheduledJobId, setScheduledJobId] = useState<string | null>(null);
  // Temporary media inputs for LinkedIn Phase 1 (single image)
  const [liImageUrl, setLiImageUrl] = useState<string>('');
  const [liImageAlt, setLiImageAlt] = useState<string>('');
  // Minimal Facebook/Instagram inputs
  const [fbPageIdInput, setFbPageIdInput] = useState<string>(''); // optional; falls back to first connected Page
  const [fbMediaUrl, setFbMediaUrl] = useState<string>(''); // optional
  const [isPublishingFacebook, setIsPublishingFacebook] = useState<boolean>(false);
  const [igUserIdInput, setIgUserIdInput] = useState<string>(''); // optional; falls back to first IG account
  const [igMediaUrl, setIgMediaUrl] = useState<string>(''); // required for IG
  const [igIsVideo, setIgIsVideo] = useState<boolean>(false);
  const [igIsReel, setIgIsReel] = useState<boolean>(false);
  const [isPublishingInstagram, setIsPublishingInstagram] = useState<boolean>(false);
  // Media library picker state
  const [showMediaPicker, setShowMediaPicker] = useState<boolean>(false);
  const [mediaLoading, setMediaLoading] = useState<boolean>(false);
  const [mediaError, setMediaError] = useState<string>('');
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);

  // LinkedIn writing style state
  const [linkedInStyle, setLinkedInStyle] = useState<string>('thought-leadership');
  
  // Ideogram image generation state
  type GeneratedImage = { id: string; url: string; prompt: string };
  const [showIdeogramModal, setShowIdeogramModal] = useState<boolean>(false);
  const [ideogramLoading, setIdeogramLoading] = useState<boolean>(false);
  const [ideogramResults, setIdeogramResults] = useState<GeneratedImage[]>([]);
  const [ideogramPrompt, setIdeogramPrompt] = useState<string>('');
  
  // Defensive: if any mediaItems have a non-absolute URL, normalize to a signed Supabase URL
  useEffect(() => {
    const needsFix = mediaItems.some((m) => !(m.file_url && /^https?:\/\//i.test(m.file_url)));
    if (!needsFix) return;
    let cancelled = false;
    (async () => {
      const fixed = await Promise.all(mediaItems.map(async (m) => {
        if (m.file_url && /^https?:\/\//i.test(m.file_url)) return m;
        try {
          const signed = await socialMediaPlannerService.ensureSignedUrl(m as any);
          if (signed && signed !== m.file_url) {
            return { ...m, file_url: signed } as MediaItem;
          }
        } catch {}
        return m;
      }));
      if (!cancelled) setMediaItems(fixed);
    })();
    return () => { cancelled = true; };
  }, [mediaItems]);
  
  // Platform preview state
  const [activePreviewPlatform, setActivePreviewPlatform] = useState<SocialPlatform | null>(null);
  const [showPlatformPreviews, setShowPlatformPreviews] = useState<boolean>(false);
  
  // Formatting helpers
  const applyFormatting = (format: 'bold' | 'italic' | 'hashtag'): void => {
    const textArea = showPlatformPreviews && activePreviewPlatform 
      ? platformTextareaRef.current 
      : commonTextareaRef.current;
    
    if (!textArea) return;
    
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selectedContent = textArea.value.substring(start, end);
    
    if (start === end && format !== 'hashtag') {
      // No text selected, do nothing for bold/italic
      return;
    }
    
    let newText = '';
    let newCursorPosition = start;
    
    switch (format) {
      case 'bold':
        newText = `**${selectedContent}**`;
        newCursorPosition = start + 2 + selectedContent.length;
        break;
      case 'italic':
        newText = `*${selectedContent}*`;
        newCursorPosition = start + 1 + selectedContent.length;
        break;
      case 'hashtag':
        // For hashtag, insert # at cursor or at start of selection
        if (start === end) {
          newText = '#';
          newCursorPosition = start + 1;
        } else {
          newText = `#${selectedContent}`;
          newCursorPosition = start + 1 + selectedContent.length;
        }
        break;
    }
    
    // Apply the formatting
    const updatedValue = textArea.value.substring(0, start) + newText + textArea.value.substring(end);
    
    if (showPlatformPreviews && activePreviewPlatform) {
      // Update platform-specific content
      setPlatformSpecificContent({
        ...platformSpecificContent,
        [activePreviewPlatform]: updatedValue
      });
    } else {
      // Update common content
      setFormState({
        ...formState,
        content: updatedValue
      });
    }
    
    // Reset cursor position after state update
    setTimeout(() => {
      if (textArea) {
        textArea.focus();
        textArea.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };
  
  

  // Service initialization - defined once to prevent duplicate declarations
  const socialMediaPlannerService = new SocialMediaPlannerService();

  // Load media library on open
  useEffect(() => {
    const loadMedia = async () => {
      if (!showMediaPicker) return;
      try {
        setMediaLoading(true);
        setMediaError('');
        const items = await socialMediaPlannerService.getMediaLibrary(50, 0);
        // Normalize to absolute signed URLs for thumbnails
        const normalized = await Promise.all(items.map(async (m) => ({
          ...m,
          file_url: await socialMediaPlannerService.ensureSignedUrl(m)
        })));
        setMediaItems(normalized.filter((m) => (m.file_type === 'image' || (m.file_url || '').match(/\.(png|jpe?g|gif|webp)(\?|$)/i))));
      } catch (err: any) {
        const msg = err?.userMessage || err?.message || 'Failed to load media library';
        setMediaError(msg);
      } finally {
        setMediaLoading(false);
      }
    };
    loadMedia();
  }, [showMediaPicker]);

  // Initialize ideogram prompt from content when opening modal
  useEffect(() => {
    if (showIdeogramModal) {
      setIdeogramPrompt((formState.content || '').trim());
    }
  }, [showIdeogramModal]);

  // Generate images via backend Ideogram proxy
  const handleGenerateIdeogram = async () => {
    try {
      const prompt = (ideogramPrompt || formState.content || '').trim();
      if (!prompt) {
        toast.error('Add some post content or enter a prompt to generate an image');
        return;
      }

      setIdeogramLoading(true);
      const userId = await getToken();
      if (!userId) {
        toast.error('Authentication error. Please log in again.');
        return;
      }

      const res = await fetch(`${ENV.API_URL}/api/marketing/logo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, prompt, size: 1024 })
      });
      const data = await res.json();

      // Support both Ideogram format and legacy format
      if (data && Array.isArray(data.data) && data.data.length > 0) {
        const converted: GeneratedImage[] = data.data.map((img: any, idx: number) => ({
          id: `gen-${Date.now()}-${idx}`,
          url: img.url,
          prompt: img.prompt || prompt,
        }));
        setIdeogramResults(converted);
        toast.success('AI images generated');
      } else if (data && Array.isArray(data.logos) && data.logos.length > 0) {
        const legacy: GeneratedImage[] = data.logos.map((l: any) => ({ id: l.id, url: l.url, prompt: l.prompt || prompt }));
        setIdeogramResults(legacy);
        toast.success('AI images generated');
      } else {
        toast.error(data?.error || 'Failed to generate images');
      }
    } catch (e: any) {
      toast.error('Image generation failed');
    } finally {
      setIdeogramLoading(false);
    }
  };

  const handleUseGeneratedImage = (url: string) => {
    if (!url) return;
    setLiImageUrl(url);
    setShowIdeogramModal(false);
    toast.success('Image inserted into post');
  };

  // Publish immediately to Facebook Page (uses optional Page ID and media URL)
  const handlePublishFacebookNow = async () => {
    try {
      if (!formState.platforms.includes('facebook')) {
        toast.error('Select Facebook to enable immediate publishing');
        return;
      }
      if (isScheduled) {
        toast.error('Immediate publish is only available when scheduling is off');
        return;
      }
      const content = withFooter((platformSpecificContent['facebook']?.trim()) || formState.content?.trim() || '');
      if (!content && !fbMediaUrl.trim()) {
        toast.error('Enter content or provide a media/link URL');
        return;
      }
      setIsPublishingFacebook(true);
      const mediaUrls = fbMediaUrl && /^https?:\/\//i.test(fbMediaUrl.trim()) ? [fbMediaUrl.trim()] : [];
      await socialMediaPlannerService.publishToFacebookNow({ pageId: fbPageIdInput.trim(), content, media_urls: mediaUrls });
      toast.success('Published to Facebook');
    } catch (err: any) {
      const msg = err?.userMessage || err?.message || 'Failed to publish to Facebook';
      toast.error(msg);
    } finally {
      setIsPublishingFacebook(false);
    }
  };

  // Publish immediately to Instagram (requires media URL)
  const handlePublishInstagramNow = async () => {
    try {
      if (!formState.platforms.includes('instagram')) {
        toast.error('Select Instagram to enable immediate publishing');
        return;
      }
      if (isScheduled) {
        toast.error('Immediate publish is only available when scheduling is off');
        return;
      }
      if (!igMediaUrl || !/^https?:\/\//i.test(igMediaUrl.trim())) {
        toast.error('Provide a valid https media URL for Instagram');
        return;
      }
      const content = withFooter((platformSpecificContent['instagram']?.trim()) || formState.content?.trim() || '');
      setIsPublishingInstagram(true);
      await socialMediaPlannerService.publishToInstagramNow({
        ig_user_id: igUserIdInput.trim() || undefined,
        content,
        media_url: igMediaUrl.trim(),
        is_video: igIsVideo,
        is_reel: igIsReel,
      });
      toast.success('Published to Instagram');
    } catch (err: any) {
      const msg = err?.userMessage || err?.message || 'Failed to publish to Instagram';
      toast.error(msg);
    } finally {
      setIsPublishingInstagram(false);
    }
  };
  
  // Publish immediately to LinkedIn (text-only)
  const handlePublishLinkedInNow = async () => {
    try {
      // Basic guards
      if (!formState.platforms.includes('linkedin')) {
        toast.error('Select LinkedIn to enable immediate publishing');
        return;
      }
      if (isScheduled) {
        toast.error('Immediate publish is only available for non-scheduled posts');
        return;
      }

      const content = withFooter((platformSpecificContent['linkedin']?.trim()) || formState.content?.trim() || '');
      if (!content) {
        toast.error('Please enter content to publish');
        return;
      }

      // Optional image validation if user selected one for LinkedIn
      let media: { media_urls?: string[]; image_alt?: string } | undefined = undefined;
      if (liImageUrl && liImageUrl.trim()) {
        const img = liImageUrl.trim();
        if (!/^https?:\/\//i.test(img)) {
          toast.error('Image URL must start with http(s)');
          return;
        }
        media = { media_urls: [img], image_alt: liImageAlt?.trim() || undefined };
      }

      setIsPublishingLinkedIn(true);
      const { urn } = await socialMediaPlannerService.publishToLinkedInNow(content, media);
      if (urn) {
        toast.success(`Published to LinkedIn (URN: ${urn})`);
      } else {
        toast.success('Published to LinkedIn');
      }
    } catch (err: any) {
      const msg = err?.userMessage || err?.message || 'Failed to publish to LinkedIn';
      toast.error(msg);
    } finally {
      setIsPublishingLinkedIn(false);
    }
  };
  
  // Get platform-specific character limit information
  const getPlatformCharacterLimit = (platform: SocialPlatform, content: string): string => {
    const limits: Record<SocialPlatform, number> = {
      facebook: 63206,
      instagram: 2200,
      linkedin: 3000,
      pinterest: 500,
      tiktok: 2200,
      youtube: 5000
    };

    const limit = limits[platform] || 280;
    const remaining = limit - content.length;

    if (remaining < 0) {
      return `${Math.abs(remaining)} characters over ${platform} limit`;
    }

    return `${content.length}/${limit} characters${remaining < 20 ? ' (limit approaching)' : ''}`;
  };

  // Load post data if editing
  useEffect(() => {
    if (post) {
      setFormState({
        ...post,
        // Ensure we have default values for optional fields
        platforms: post.platforms || [],
        hashtags: post.hashtags || [],
        media_urls: post.media_urls || []
      });

      // Set platform specific content
      if (post.platform_details && post.platform_details.length > 0) {
        const platformContent: Partial<Record<SocialPlatform, string>> = {
          facebook: '',
          linkedin: '',
          instagram: '',
          pinterest: '',
          tiktok: '',
          youtube: ''
        };

        post.platform_details.forEach(detail => {
          if (detail.platform_specific_content) {
            platformContent[detail.platform] = detail.platform_specific_content;
          }
        });

        setPlatformSpecificContent(platformContent);
      }

      // Set scheduling if applicable
      if (post.scheduled_for) {
        setIsScheduled(true);
        const date = new Date(post.scheduled_for);
        setScheduleDate(format(date, 'yyyy-MM-dd'));
        setScheduleTime(format(date, 'HH:mm'));
      }
    }
  }, [post]);

  // Handle platform toggle
  const handlePlatformToggle = (platform: SocialPlatform) => {
    let updatedPlatforms = [...formState.platforms];
    
    if (updatedPlatforms.includes(platform)) {
      // Remove platform if already selected
      updatedPlatforms = updatedPlatforms.filter(p => p !== platform);
    } else {
      // Add platform if not already selected
      updatedPlatforms.push(platform);
    }
    
    setFormState({ ...formState, platforms: updatedPlatforms });
  };

  // Handle opening Claude modal for content generation
  const handleOpenClaudeModal = () => {
    if (formState.platforms.length === 0) {
      toast.error('Please select at least one platform before generating content');
      return;
    }
    setShowClaudeModal(true);
  };
  
  // Handle content generated from Claude
  const handleClaudeContentGenerated = (content: string, hashtags?: string[]) => {
    // If we're in platform-specific mode, update that platform's content
    if (showPlatformPreviews && activePreviewPlatform) {
      setPlatformSpecificContent({
        ...platformSpecificContent,
        [activePreviewPlatform]: content
      });
    } else {
      // Otherwise update the main content
      setFormState(prevState => ({ 
        ...prevState, 
        content 
      }));
      
      // Hashtag auto-insert disabled for cleaner UI
    }
  };
  
  // AI content generation handled by Claude modal
  
  // Generate a recurrence summary for display
  const generateRecurrenceSummary = (): string => {
    return 'This post will not repeat.';
  };

  // Generate RFC 5545 iCalendar recurrence rule
  const generateRecurrenceRule = (): string | undefined => {
    return undefined;
  };

  // Add renderPlatformButton function
  const renderPlatformButton = (platform: SocialPlatform) => {
    const isSelected = formState.platforms.includes(platform);
    
    // Platform-specific styles
    const styles: Record<SocialPlatform, { selected: string, unselected: string }> = {
      facebook: {
        selected: 'bg-blue-600 text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      },
      linkedin: {
        selected: 'bg-blue-800 text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      },
      pinterest: {
        selected: 'bg-red-600 text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      },
      tiktok: {
        selected: 'bg-black text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      },
      instagram: {
        selected: 'bg-purple-600 text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      },
      youtube: {
        selected: 'bg-red-700 text-white',
        unselected: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }
    };
    
    const iconText: Record<string, string> = {
      facebook: 'f',
      linkedin: 'in',
      instagram: 'ig',
      pinterest: 'p',
      tiktok: 'tt',
      youtube: 'yt'
    };
    
    return (
      <button
        type="button"
        onClick={() => handlePlatformToggle(platform)}
        className={`p-3 rounded-full ${isSelected ? styles[platform].selected : styles[platform].unselected} flex items-center justify-center w-10 h-10`}
      >
        <span className="font-bold text-sm">{iconText[platform]}</span>
      </button>
    );
  };

  // Handle submit
  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate required fields
      if (!formState.content.trim()) {
        setError('Please enter content for your post');
        setIsLoading(false);
        return;
      }

      if (formState.platforms.length === 0) {
        setError('Please select at least one platform');
        setIsLoading(false);
        return;
      }

      // Prepare the post data
      const postData: SocialPost = {
        ...formState,
        status: isScheduled ? 'scheduled' : 'draft'
      };

      // Add scheduled date if applicable
      if (isScheduled && scheduleDate && scheduleTime) {
        const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
        postData.scheduled_for = scheduledDateTime.toISOString();
      } else {
        postData.scheduled_for = undefined;
      }
      
      // Add platform-specific variations
      const platformVariations: Record<string, { platform_specific_content: string, scheduled_for?: string }> = {};
      
      // Process platform-specific content
      formState.platforms.forEach((platform) => {
        const platformContent = platformSpecificContent[platform];
        if (platformContent && platformContent.trim() && platformContent !== formState.content) {
          platformVariations[platform] = {
            platform_specific_content: withFooter(platformContent),
            scheduled_for: postData.scheduled_for || undefined // Default to same schedule, ensure type safety
          };
        }
      });
      
      // Save the post
      onSave(postData);
      
    } catch (error) {
      console.error('Error saving post:', error);
      setError('Failed to save post');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {post ? 'Edit Post' : 'Create New Post'}
          </h2>
          
          <button 
            onClick={onClose}
            className="rounded-full w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300"
            aria-label="Close"
          >
            <span aria-hidden="true" className="text-gray-600">&times;</span>
          </button>
        </div>

        {/* Post Content */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">Post Content</label>
            <button
              type="button"
              onClick={() => handleOpenClaudeModal()}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 shadow-md transition-all flex items-center gap-2"
            >
              <span className="text-xl">✨</span>
              Generate with AI
            </button>
          </div>
          <textarea
            value={formState.content}
            onChange={(e) => setFormState({ ...formState, content: e.target.value })}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Write your post or click 'Generate with AI' to create content automatically..."
          />
          <div className="mt-1 text-xs text-gray-500">This base content will be used for all platforms unless customized below.</div>
        </div>

        {/* Media Picker Modal */}
        {showMediaPicker && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" role="dialog" aria-modal="true">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-4 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Media Library</h3>
                <button
                  onClick={() => setShowMediaPicker(false)}
                  className="rounded-full w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300"
                  aria-label="Close media picker"
                >
                  <span aria-hidden="true" className="text-gray-600">&times;</span>
                </button>
              </div>
              {/* Upload new media inline */}
              <div className="mb-4 border border-gray-200 rounded-md p-3">
                <div className="text-sm font-medium mb-2">Upload New Media</div>
                <MediaUploaderTailwind
                  onMediaUpload={async (uploaded) => {
                    try {
                      // Normalize to signed absolute URL
                      const absUrl = await socialMediaPlannerService.ensureSignedUrl(uploaded as any);
                      // Update local library list (prepend)
                      const normalizedUploaded = { ...uploaded, file_url: absUrl } as any;
                      setMediaItems((prev) => [normalizedUploaded, ...prev]);
                      // Auto-select for LinkedIn
                      setLiImageUrl(absUrl);
                      if (!liImageAlt && (uploaded as any).alt_text) setLiImageAlt((uploaded as any).alt_text);
                      setShowMediaPicker(false);
                      toast.success('Image uploaded and selected');
                    } catch (e) {
                      toast.error('Uploaded, but failed to prepare preview URL');
                    }
                  }}
                  acceptedFileTypes={[
                    'image/jpeg', 'image/png', 'image/gif', 'image/webp'
                  ]}
                  maxSizeInBytes={10 * 1024 * 1024}
                />
              </div>
              {mediaError && (
                <div className="text-sm text-red-600 mb-2">{mediaError}</div>
              )}

        {/* Facebook Quick Inputs */}
        {formState.platforms.includes('facebook') && (
          <div className="mt-4 p-3 border border-blue-100 rounded-md bg-blue-50">
            <div className="text-sm font-medium text-blue-900 mb-2">Facebook Options (optional)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-blue-900 mb-1">Page ID (optional)</label>
                <input
                  type="text"
                  value={fbPageIdInput}
                  onChange={(e) => setFbPageIdInput(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Leave empty to use first connected Page"
                />
              </div>
              <div>
                <label className="block text-xs text-blue-900 mb-1">Media/Link URL (optional)</label>
                <input
                  type="url"
                  value={fbMediaUrl}
                  onChange={(e) => setFbMediaUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="https://example.com/image.jpg or page link"
                />
              </div>
            </div>
          </div>
        )}

        {/* Instagram Quick Inputs */}
        {formState.platforms.includes('instagram') && (
          <div className="mt-4 p-3 border border-purple-100 rounded-md bg-purple-50">
            <div className="text-sm font-medium text-purple-900 mb-2">Instagram Options</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-purple-900 mb-1">IG User ID (optional)</label>
                <input
                  type="text"
                  value={igUserIdInput}
                  onChange={(e) => setIgUserIdInput(e.target.value)}
                  className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="Leave empty to use first connected IG account"
                />
              </div>
              <div>
                <label className="block text-xs text-purple-900 mb-1">Media URL (required)</label>
                <input
                  type="url"
                  value={igMediaUrl}
                  onChange={(e) => setIgMediaUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="https://example.com/image.jpg or https://.../video.mp4"
                />
              </div>
              <div className="flex items-center space-x-4 md:col-span-2">
                <label className="inline-flex items-center text-xs text-purple-900">
                  <input type="checkbox" className="mr-2" checked={igIsVideo} onChange={(e) => setIgIsVideo(e.target.checked)} />
                  Video
                </label>
                <label className="inline-flex items-center text-xs text-purple-900">
                  <input type="checkbox" className="mr-2" checked={igIsReel} onChange={(e) => setIgIsReel(e.target.checked)} />
                  Reels
                </label>
              </div>
            </div>
          </div>
        )}
              {mediaLoading ? (
                <div className="text-sm text-gray-600">Loading media…</div>
              ) : (
                <>
                  {mediaItems.length === 0 ? (
                    <div className="text-sm text-gray-600">No media found. Upload images via your media library first.</div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {mediaItems.filter((it) => !!(it.file_url && /^https?:\/\//i.test(it.file_url))).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className="border rounded-md overflow-hidden hover:ring-2 hover:ring-blue-500 focus:outline-none"
                          onClick={async () => {
                            const absUrl = await socialMediaPlannerService.ensureSignedUrl(item);
                            setLiImageUrl(absUrl);
                            if (!liImageAlt && item.alt_text) setLiImageAlt(item.alt_text);
                            setShowMediaPicker(false);
                            toast.success('Image selected');
                          }}
                          title={item.file_name}
                        >
                          <img src={item.file_url} alt={item.alt_text || item.file_name} className="w-full h-28 object-cover" />
                          <div className="p-2 text-left">
                            <div className="text-xs font-medium truncate" title={item.file_name}>{item.file_name}</div>
                            {item.alt_text && <div className="text-[11px] text-gray-500 truncate" title={item.alt_text}>{item.alt_text}</div>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Ideogram Modal */}
        {showIdeogramModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[85vh] overflow-auto p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Generate Image with AI</h3>
                <button
                  type="button"
                  onClick={() => setShowIdeogramModal(false)}
                  className="rounded-full w-8 h-8 flex items-center justify-center bg-gray-200 hover:bg-gray-300"
                  aria-label="Close AI image modal"
                >
                  <span aria-hidden="true" className="text-gray-600">&times;</span>
                </button>
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Prompt</label>
                <textarea
                  rows={3}
                  className="w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={ideogramPrompt}
                  onChange={(e) => setIdeogramPrompt(e.target.value)}
                  placeholder="Describe the image you want to generate"
                />
              </div>
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={handleGenerateIdeogram}
                  className="px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-60"
                  disabled={ideogramLoading}
                >
                  {ideogramLoading ? 'Generating…' : 'Generate'}
                </button>
                {ideogramResults.length > 0 && (
                  <div className="text-sm text-gray-600">{ideogramResults.length} result(s)</div>
                )}
              </div>
              {ideogramResults.length === 0 ? (
                <div className="text-sm text-gray-600">Enter a prompt and click Generate.</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {ideogramResults.map((img) => (
                    <div key={img.id} className="border rounded-md overflow-hidden">
                      <img src={img.url} alt="AI generated" className="w-full h-auto" />
                      <div className="p-2 flex justify-between items-center">
                        <button
                          type="button"
                          onClick={() => handleUseGeneratedImage(img.url)}
                          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Use image
                        </button>
                        <a
                          href={img.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-gray-600 hover:underline"
                        >
                          Open
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Scheduling (Local -> UTC conversion) */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Scheduling</label>
            <div className="flex items-center">
              <input
                id="toggle-schedule"
                type="checkbox"
                checked={isScheduled}
                onChange={(e) => setIsScheduled(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="toggle-schedule" className="text-sm text-gray-700">Schedule this post</label>
            </div>
          </div>
          {isScheduled && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date (Your timezone)</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Time (Your timezone)</label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="md:col-span-2 text-xs text-gray-500">Your time will be converted to UTC automatically for exact-time delivery.</div>
            </div>
          )}
        </div>
        
        {/* Platform Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Platforms
          </label>
          <div className="flex space-x-4">
            {renderPlatformButton('facebook')}
            {renderPlatformButton('linkedin')}
            {renderPlatformButton('instagram')}
          </div>
          
          {/* Selected platforms display */}
          {formState.platforms.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {formState.platforms.map((platform) => (
                <div 
                  key={platform} 
                  className="flex items-center bg-gray-100 rounded px-2 py-1"
                >
                  <span className="capitalize mr-1">{platform}</span>
                  <button 
                    onClick={() => handlePlatformToggle(platform as SocialPlatform)}
                    className="ml-1 text-gray-500 hover:text-gray-700"
                    aria-label={`Remove ${platform}`}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* LinkedIn Writing Style Selector */}
          {formState.platforms.includes('linkedin') && (
            <div className="mt-4 p-3 border border-blue-100 rounded-md bg-blue-50">
              <label className="block text-sm font-medium text-blue-900 mb-2">
                LinkedIn Writing Style
              </label>
              <select
                value={linkedInStyle}
                onChange={(e) => setLinkedInStyle(e.target.value)}
                className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="thought-leadership">💡 Thought Leadership - Expert insights and predictions</option>
                <option value="storytelling">📖 Storytelling - Narrative-driven content</option>
                <option value="problem-solution">🔧 Problem-Solution - Define problems + solutions</option>
                <option value="industry-insights">📊 Industry Insights - Trends and analysis</option>
                <option value="personal-experience">👤 Personal Experience - Professional journey lessons</option>
                <option value="data-driven">📈 Data-Driven - Statistics and case studies</option>
                <option value="controversial-take">⚡ Controversial Take - Challenge industry norms</option>
              </select>
              <p className="mt-1 text-xs text-blue-700">
                This style guides how AI generates your LinkedIn content
              </p>
            </div>
          )}
          
          {/* LinkedIn image attachment (Phase 1) */}
          {formState.platforms.includes('linkedin') && (
            <div className="mt-4 p-3 border border-blue-100 rounded-md bg-blue-50">
              <div className="text-sm font-medium text-blue-900 mb-2">LinkedIn Image (optional)</div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="block text-xs text-blue-900 mb-1">Image URL</label>
                  <input
                    type="url"
                    value={liImageUrl}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLiImageUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-blue-200 rounded-md"
                  />
                  {/* Thumbnail preview */}
                  {liImageUrl && /^https?:\/\//i.test(liImageUrl) && (
                    <div className="mt-2 flex items-center gap-3">
                      <img src={liImageUrl} alt={liImageAlt || 'Selected image'} className="w-20 h-20 object-cover rounded border border-blue-200" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline"
                        onClick={() => { setLiImageUrl(''); }}
                      >Remove</button>
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-blue-900 mb-1">Alt text (accessibility)</label>
                  <input
                    type="text"
                    value={liImageAlt}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLiImageAlt(e.target.value)}
                    placeholder="Describe the image for screen readers"
                    className="w-full px-3 py-2 border border-blue-200 rounded-md"
                  />
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowMediaPicker(true)}
                      className="text-xs text-blue-700 hover:underline"
                    >
                      Choose from media library
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowIdeogramModal(true)}
                      className="text-xs text-purple-700 hover:underline"
                    >
                      Generate image with AI
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Platform content */}
              <div className="p-4">
                {formState.platforms.length > 0 && activePreviewPlatform ? (
                  <div>
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {activePreviewPlatform.charAt(0).toUpperCase() + activePreviewPlatform.slice(1)} Content
                      </label>
                      
                      {/* Platform-specific formatting toolbar */}
                      <div className="flex flex-wrap items-center justify-between mb-4">
                        <button
                          type="button"
                          onClick={() => applyFormatting('bold')}
                          className="p-1 mr-1 hover:bg-gray-200 rounded"
                          title="Bold"
                        >
                          <span className="font-bold text-sm">B</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => applyFormatting('italic')}
                          className="p-1 mr-1 hover:bg-gray-200 rounded"
                          title="Italic"
                        >
                          <span className="italic text-sm">I</span>
                        </button>
                        {/* Hashtag buttons removed for cleaner UI */}
                        <span className="text-xs text-gray-500 ml-2">Format selected text</span>
                      </div>
                      
                      <textarea
                        ref={platformTextareaRef}
                        value={platformSpecificContent[activePreviewPlatform] || formState.content}
                        onChange={(e) => setPlatformSpecificContent({
                          ...platformSpecificContent,
                          [activePreviewPlatform]: e.target.value
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-b-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Customize your ${activePreviewPlatform} content...`}
                      ></textarea>
                    </div>
                    
                    {/* Platform preview */}
                    <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                      <h5 className="text-sm font-medium mb-2">Preview</h5>
                      <div className="border rounded-md p-3 bg-white">
                        <div className="flex items-center mb-2">
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center mr-2">
                            <span className="font-bold text-xs text-gray-600">{activePreviewPlatform.charAt(0).toUpperCase()}</span>
                          </div>
                          <span className="font-medium text-sm">Your {activePreviewPlatform.charAt(0).toUpperCase() + activePreviewPlatform.slice(1)} Page</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">
                          {withFooter(platformSpecificContent[activePreviewPlatform] || formState.content)}
                        </p>
                      </div>
                      
                      {/* Character limits and platform-specific tips */}
                      <div className="mt-3 flex justify-between items-center">
                        <span className="text-xs text-gray-500">{getPlatformCharacterLimit(activePreviewPlatform, platformSpecificContent[activePreviewPlatform] || formState.content)}</span>
                        <button
                          type="button"
                          onClick={() => handleOpenClaudeModal()}
                          className="px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 shadow-md transition-all flex items-center gap-2"
                        >
                          <span>✨</span>
                          Optimize for {activePreviewPlatform}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {formState.platforms.length === 0 ? 
                      'Select at least one platform above to create platform-specific content' : 
                      'Select a platform tab to customize content'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button 
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          {formState.platforms.includes('linkedin') && !isScheduled && (
            <button
              onClick={handlePublishLinkedInNow}
              disabled={isPublishingLinkedIn}
              className={`px-4 py-2 ${isPublishingLinkedIn ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-md`}
            >
              {isPublishingLinkedIn ? 'Publishing…' : 'Publish to LinkedIn now'}
            </button>
          )}
          {formState.platforms.includes('linkedin') && isScheduled && !scheduledJobId && (
            <button
              onClick={handleScheduleLinkedIn}
              disabled={isScheduling}
              className={`px-4 py-2 ${isScheduling ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'} text-white rounded-md`}
            >
              {isScheduling ? 'Scheduling…' : 'Schedule for LinkedIn'}
            </button>
          )}
          {formState.platforms.includes('linkedin') && isScheduled && !!scheduledJobId && (
            <button
              onClick={handleRescheduleLinkedIn}
              disabled={isScheduling}
              className={`px-4 py-2 ${isScheduling ? 'bg-gray-400' : 'bg-amber-600 hover:bg-amber-700'} text-white rounded-md`}
            >
              {isScheduling ? 'Updating…' : 'Update LinkedIn schedule'}
            </button>
          )}
          <button 
            onClick={() => handleSubmit()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Save Post
          </button>
          {formState.platforms.includes('facebook') && !isScheduled && (
            <button
              onClick={handlePublishFacebookNow}
              disabled={isPublishingFacebook}
              className={`px-4 py-2 ${isPublishingFacebook ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-md`}
            >
              {isPublishingFacebook ? 'Publishing…' : 'Publish to Facebook now'}
            </button>
          )}
          {formState.platforms.includes('instagram') && !isScheduled && (
            <button
              onClick={handlePublishInstagramNow}
              disabled={isPublishingInstagram}
              className={`px-4 py-2 ${isPublishingInstagram ? 'bg-gray-400' : 'bg-purple-600 hover:bg-purple-700'} text-white rounded-md`}
            >
              {isPublishingInstagram ? 'Publishing…' : 'Publish to Instagram now'}
            </button>
          )}
        </div>
      </div>
      {/* Claude Content Generation Modal */}
      {showClaudeModal && (
        <ClaudeContentModal
          platforms={formState.platforms}
          onClose={() => setShowClaudeModal(false)}
          onContentGenerated={handleClaudeContentGenerated}
          existingContent={showPlatformPreviews && activePreviewPlatform ? 
            withFooter(platformSpecificContent[activePreviewPlatform] || formState.content) : 
            withFooter(formState.content)}
          linkedInStyle={formState.platforms.includes('linkedin') ? linkedInStyle : undefined}
        />
      )}
      
      {/* Hashtag collections removed for cleaner UI */}
    </div>
  );
};

export default PostComposer;
